import React, { useEffect, useSyncExternalStore } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { useGetPuck } from '@puckeditor/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import {
  getVariableCatalogSnapshot,
  getVariableCatalogSource,
  loadVariableCatalog,
  subscribeVariableCatalog,
} from '../utils/variableCatalogRegistry';
import { variableImageProp } from '../utils/variableNodes';
import { updateSlot } from '../utils/imageSlots';
import placeholderImage from './variable-image-placeholder.svg';
import styles from './VariableImage.module.css';

// Rien à relire sans document rattaché, ni dans un modèle.
const canRefreshInCatalog = () => {
  const source = getVariableCatalogSource();
  return !!source && source.type !== 'web_template';
};

/**
 * « Mettre à jour », comme le `ButtonRefresh` de l'image variable du
 * classique : seulement quand le CRM a une autre valeur que celle copiée dans
 * le bloc, et jamais dans un modèle, dont les variables n'ont pas de valeur.
 * `slot` désigne l'image dans le bloc (`imageSlots`), à plat ou dans un média.
 */
function VariableImageRefresh({
  blockId, slot, binding, className = styles.refresh,
}) {
  const getPuck = useGetPuck();
  const { catalog } = useSyncExternalStore(subscribeVariableCatalog, getVariableCatalogSnapshot);
  useEffect(() => { loadVariableCatalog(); }, []);

  const fresh = catalog.byKey[binding.variableKey];
  if (!fresh || (fresh.value || '') === (binding.variableValue || '')) return null;

  const refresh = async (event) => {
    event.stopPropagation();
    const { byKey } = await loadVariableCatalog({ force: true });
    // Catalogue illisible : on garde l'image plutôt que de l'effacer.
    if (!byKey[binding.variableKey]) return;
    const value = byKey[binding.variableKey].value || '';
    const { dispatch, getItemById, getSelectorForId } = getPuck();
    const item = getItemById(blockId);
    const selector = getSelectorForId(blockId);
    if (!item || !selector) return;
    dispatch({
      type: 'replace',
      destinationZone: selector.zone,
      destinationIndex: selector.index,
      data: {
        type: item.type,
        props: updateSlot(item.props, slot, (object) => ({
          ...object,
          [slot.urlProp]: value,
          [variableImageProp(slot.urlProp)]: { ...binding, variableValue: value },
        })),
      },
    });
  };

  return (
    <Tooltip label="Mettre à jour" withinPortal={false}>
      <ActionIcon
        className={className}
        size="sm"
        radius="xl"
        variant="default"
        aria-label="Mettre à jour l'image"
        onClick={refresh}
      >
        <FontAwesomeIcon icon={icon({ name: 'arrows-rotate', style: 'light' })} style={{ fontSize: 12 }} />
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * Image d'un bloc, branchée ou non sur une variable du CRM (`urlProp` +
 * `${urlProp}Variable`).
 *
 * Variable sans valeur (modèle, ou champ vide dans le CRM) : l'éditeur montre
 * l'image d'attente du classique marquée de l'éclair Smart4, dessiné dans
 * l'image pour rester sur elle quelle que soit la mise en page du bloc ; le nom
 * de la variable se lit dans le sélecteur. Le site publié n'affiche rien.
 *
 * `renderImage(src)` dessine l'image à la taille du bloc ; il reçoit `''` quand
 * le bloc n'a pas d'image, à lui de décider quoi montrer.
 *
 * `variableBox` : le bloc est une box variable du formulaire.
 */
export default function VariableImage({
  blockId, urlProp, src, binding, isEditing, renderImage, variableBox = false,
}) {
  const bound = !!binding?.variableKey;
  // Box variable encore vide (img_variable du classique) : même attente qu'une
  // variable sans valeur, le formulaire la remplira.
  const emptyBox = variableBox && !src;
  const waiting = (bound && !binding.variableValue) || emptyBox;

  if (!isEditing) return waiting ? null : renderImage(src);
  if (!bound) return renderImage(emptyBox ? placeholderImage : src);

  return (
    <div className={styles.frame}>
      {renderImage(waiting ? placeholderImage : src)}
      {canRefreshInCatalog() && (
        <VariableImageRefresh blockId={blockId} slot={{ path: '', urlProp, shape: 'flat' }} binding={binding} />
      )}
    </div>
  );
}

/**
 * « Mettre à jour » d'un média de fond (Bloc, slide) branché sur le CRM, dans
 * le coin du cadre : le fond ne se survole pas, le contenu le recouvre.
 * `path` mène à l'objet média dans les props du bloc.
 */
export function VariableMediaRefresh({
  blockId, path, media, isEditing,
}) {
  const binding = media?.[variableImageProp('src')];
  if (!isEditing || !binding?.variableKey || !canRefreshInCatalog()) return null;
  return (
    <div className={styles.corner}>
      <VariableImageRefresh
        blockId={blockId}
        slot={{ path, urlProp: 'src', shape: 'media' }}
        binding={binding}
        className=""
      />
    </div>
  );
}
